import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * Modal d’envoi d’un message interne (case_messages) depuis la fiche IPR.
 */
export default function IprSendMessageModal({
  open,
  onClose,
  onSend,
  caseNumber,
  patientName,
}) {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) {
      setContent('');
      setSending(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !sending) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose, sending]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const trimmed = content.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await onSend(trimmed);
      onClose?.();
    } catch {
      // toast géré par le parent
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label="Fermer"
        onClick={() => !sending && onClose?.()}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ipr-send-message-title"
        className="relative w-full max-w-lg rounded-xl border border-border bg-background shadow-xl"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 id="ipr-send-message-title" className="text-lg font-semibold text-foreground">
              Envoyer un message
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5 truncate">
              Messagerie interne · {caseNumber || 'cas'}
              {patientName ? ` · ${patientName}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !sending && onClose?.()}
            className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center shrink-0 cursor-pointer"
            aria-label="Fermer"
          >
            <Icon name="X" size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label htmlFor="ipr-message-content" className="text-sm font-medium text-foreground mb-2 block">
              Message
            </label>
            <textarea
              id="ipr-message-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              disabled={sending}
              placeholder="Écrivez votre message concernant la fiche IPR…"
              className="w-full min-h-[120px] px-3 py-2.5 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-y"
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              Le message sera visible dans l’onglet Messages du dossier.
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose?.()}
              disabled={sending}
              className="min-h-[44px] cursor-pointer"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              iconName="Send"
              loading={sending}
              disabled={sending || !content.trim()}
              className="min-h-[44px] cursor-pointer"
            >
              Envoyer
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
